// Copyright (c) 2020 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import * as React from "react";
import { connect } from "react-redux";
import { WrappedComponentProps, injectIntl, FormattedMessage } from "react-intl";
import { Button } from "react-bootstrap";

import { IAction, ActionType, raiseAction } from "actions";
import { isSet } from "utils";
import { TimePeriod, DashboardPanel } from "types";
import { formatDate, getPanelSelection } from "guiutils";
import { IState } from "states/states";
import { GraphViewMode } from "states/cachedstates";

import EnergyGraph from "view/comp/EnergyGraph";
import TariffCircuitSelect from "view/comp/TariffCircuitSelect";
import EnergySourceSelect from "view/comp/EnergySourceSelect";
import TrendMemberSelect from "view/comp/TrendMemberSelect";
import PeriodSelect from "view/comp/PeriodSelect";
import TrendHeading from "view/comp/TrendHeading";
import TrendDatatable from "view/comp/TrendDatatable";
import HierarchySelect from "view/comp/HierarchySelect";
import Power from "./Power";
import CategorySelect from "view/comp/CategorySelect";

// uses webpack definitions
declare const IS_CLOUD: boolean;

interface ITrendExtProps {
  panel?: DashboardPanel;
  dashboardIndex?: number;
}
interface ITrendProps {
  graphViewMode: GraphViewMode;
  selectedTimestamp: number | undefined;
  timePeriod: TimePeriod;
  trendMember: "energy" | "power" | "costs";
  selectedHierarchyId: number | null | undefined;
}

const mapStateToProps = (state: IState, ownProps: ITrendExtProps) => {
  const { index, selection } = getPanelSelection(
    state,
    ownProps.panel ?? DashboardPanel.ENERGY,
    ownProps.dashboardIndex,
  );

  const props: ITrendProps = {
    graphViewMode: selection?.graphViewMode ?? GraphViewMode.BAR,
    selectedTimestamp: state.gui.selectedTimestamp,
    timePeriod: state.gui.trendPeriod.timePeriod,
    trendMember: state.gui.trendMember,
    selectedHierarchyId: selection?.hierarchyId,
  };
  return props;
};

interface ITrendDispatch {
  onShowTimeseries: () => void;
}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: ITrendDispatch = {
    onShowTimeseries: () => {
      dispatch(raiseAction(ActionType.ACT_SELECT_TIMESTAMP, undefined));
    },
  };
  return dispatchProps;
};

class TrendComponent extends React.Component<ITrendProps & ITrendDispatch & WrappedComponentProps> {
  public render() {
    // render details of selected timestamp or the timeseries?
    if (isSet(this.props.selectedTimestamp)) {
      return this.renderTimestamp();
    }
    return this.renderTimeseries();
  }

  private renderTimeseries() {
    return (
      <div id="trend" className="flex-self flex-content">
        <div className="headerbuttons mb-2">
          <div className="left">
            <EnergySourceSelect
              className="me-2"
              panel={DashboardPanel.ENERGY}
              allowAllEnergySources={false}
            />
            <HierarchySelect
              className="me-2"
              panel={DashboardPanel.ENERGY}
              allowAllEnergySources={false}
              allowAllHierarchies={true}
            />
            <CategorySelect
              className="me-2"
              panel={DashboardPanel.ENERGY}
              allowAllEnergySources={false}
              allowAllHierarchies={this.props.selectedHierarchyId === undefined}
              includeChildren={this.props.selectedHierarchyId === undefined}
            />
            {this.props.selectedHierarchyId === undefined && (
              <TariffCircuitSelect
                className="me-2"
                panel={DashboardPanel.ENERGY}
                allowAllEnergySources={false}
                allowAllHierarchies={true}
                ignoreCategories={false}
              />
            )}
            <TrendMemberSelect />
          </div>
          {IS_CLOUD && (
            <PeriodSelect identifier="trendPeriod" updateAction={ActionType.ACT_UPDATE_TREND} />
          )}
        </div>
        <TrendHeading
          identifier="trendPeriod"
          includeDate={true}
          showTrendData={true}
          showGraphModes={[GraphViewMode.BAR, GraphViewMode.LINE, GraphViewMode.TABLE]}
        />
        {this.props.graphViewMode !== GraphViewMode.TABLE ? (
          <EnergyGraph showLegend={true} />
        ) : (
          <TrendDatatable />
        )}
      </div>
    );
  }

  private renderTimestamp() {
    return (
      <div id="trend-timestamp">
        <Button id="navigate-back" variant="secondary" onClick={this.props.onShowTimeseries}>
          <FormattedMessage id="Back to Timeseries" />
        </Button>
        <h1>
          <FormattedMessage id={`TrendMember_${this.props.trendMember}`} />{" "}
          {formatDate(
            this.props.selectedTimestamp! * 1000,
            this.props.timePeriod,
            this.props.intl.locale,
            true,
          )}
        </h1>
        <Power panel={DashboardPanel.ENERGY} />
      </div>
    );
  }
}

const Trend = connect(mapStateToProps, mapDispatchToProps)(TrendComponent);

export default injectIntl(Trend);
